<template>
  <transition name="modal-fade">
    <div class="modal-overlay">
      <div class="modal-container">
        <!-- Header -->
        <div class="modal-header">
          <h3 class="modal-title">{{ title }}</h3>
          <button class="modal-close" @click="$emit('close')" title="Cerrar">×</button>
        </div>

        <!-- Contenido del Modal -->
        <div class="modal-body">
          <!-- Editor de Texto -->
          <div v-if="componentType === 'TextBlock'" class="text-editor-container">
            <div class="editor-tabs">
              <button 
                :class="['tab-btn', { active: activeTextTab === 'content' }]"
                @click="activeTextTab = 'content'"
              >
                <i class="fas fa-edit"></i> Contenido
              </button>
              <button 
                :class="['tab-btn', { active: activeTextTab === 'style' }]"
                @click="activeTextTab = 'style'"
              >
                <i class="fas fa-palette"></i> Estilo
              </button>
            </div>

            <!-- Tab de Contenido -->
            <div v-if="activeTextTab === 'content'" class="tab-content">
              <text-editor
                :value="editedContent"
                @input="updateContent"
                class="full-height-editor"
              />
            </div>

            <!-- Tab de Estilo -->
            <div v-if="activeTextTab === 'style'" class="tab-content">
              <div class="style-grid">
                <div class="style-group">
                  <label>Tamaño de Fuente</label>
                  <input type="text" v-model="textStyles.fontSize" class="form-input">
                </div>
                <div class="style-group">
                  <label>Color de Texto</label>
                  <div class="color-picker">
                    <input type="color" v-model="textStyles.color">
                    <input type="text" v-model="textStyles.color" placeholder="#000000">
                  </div>
                </div>
                <div class="style-group">
                  <label>Color de Fondo</label>
                  <div class="color-picker">
                    <input type="color" v-model="textStyles.backgroundColor">
                    <input type="text" v-model="textStyles.backgroundColor" placeholder="transparent">
                  </div>
                </div>
                <div class="style-group">
                  <label>Alineación</label>
                  <select v-model="textStyles.textAlign" class="form-select">
                    <option value="left">Izquierda</option>
                    <option value="center">Centro</option>
                    <option value="right">Derecha</option>
                    <option value="justify">Justificado</option>
                  </select>
                </div>
                <div class="style-group">
                  <label>Familia de Fuente</label>
                  <select v-model="textStyles.fontFamily" class="form-select">
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="Helvetica, sans-serif">Helvetica</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Courier New', monospace">Courier New</option>
                  </select>
                </div>
                <div class="style-group">
                  <label>Peso de Fuente</label>
                  <select v-model="textStyles.fontWeight" class="form-select">
                    <option value="300">Ligero</option>
                    <option value="400">Normal</option>
                    <option value="500">Medio</option>
                    <option value="600">Semi-bold</option>
                    <option value="700">Bold</option>
                  </select>
                </div>
                <div class="style-group">
                  <label>Altura de Línea</label>
                  <input type="text" v-model="textStyles.lineHeight" class="form-input" placeholder="1.5">
                </div>
                <div class="style-group">
                  <label>Padding</label>
                  <input type="text" v-model="textStyles.padding" class="form-input" placeholder="16px">
                </div>
                <div class="style-group">
                  <label>Margen</label>
                  <input type="text" v-model="textStyles.margin" class="form-input" placeholder="10px 0">
                </div>
                <div class="style-group">
                  <label>Border Radius</label>
                  <input type="text" v-model="textStyles.borderRadius" class="form-input" placeholder="0px">
                </div>
                <div class="style-group">
                  <label>Borde</label>
                  <input type="text" v-model="textStyles.border" class="form-input" placeholder="none">
                </div>
              </div>
            </div>
          </div>

          <!-- Editor de Imagen -->
          <div v-if="componentType === 'ImageBlock'" class="image-editor-container">
            <div class="editor-tabs">
              <button 
                :class="['tab-btn', { active: activeImageTab === 'upload' }]"
                @click="activeImageTab = 'upload'"
              >
                <i class="fas fa-upload"></i> Imagen
              </button>
              <button 
                :class="['tab-btn', { active: activeImageTab === 'style' }]"
                @click="activeImageTab = 'style'"
              >
                <i class="fas fa-cog"></i> Configuración
              </button>
            </div>

            <!-- Tab de Upload -->
            <div v-if="activeImageTab === 'upload'" class="tab-content">
              <div class="image-url-input">
                <label>URL de la Imagen</label>
                <input 
                  type="url" 
                  v-model="editedContent" 
                  class="form-input"
                  placeholder="https://ejemplo.com/imagen.jpg"
                >
              </div>

              <div class="divider-text">O</div>

              <!-- Zona de Drop -->
              <div
                class="drop-zone"
                :class="{ 'drop-zone--active': isDragging }"
                @dragenter.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @dragover.prevent
                @drop.prevent="handleDrop"
              >
                <!-- Vista previa de imagen -->
                <div v-if="imagePreview" class="image-preview">
                  <img :src="imagePreview" alt="Vista previa" />
                  <button class="remove-image" @click="removeImage">
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <!-- Área de Drop cuando no hay imagen -->
                <div v-else class="drop-content">
                  <i class="fas fa-cloud-upload-alt"></i>
                  <p>Arrastra y suelta una imagen aquí o</p>
                  <input
                    type="file"
                    ref="fileInput"
                    accept="image/*"
                    class="file-input"
                    @change="handleFileSelect"
                  />
                  <button class="btn btn-primary" @click="$refs.fileInput.click()">
                    <i class="fas fa-upload"></i> Seleccionar Imagen
                  </button>
                </div>
              </div>
            </div>

            <!-- Tab de Configuración -->
            <div v-if="activeImageTab === 'style'" class="tab-content">
              <div class="style-grid">
                <div class="style-group">
                  <label>Ancho</label>
                  <input type="text" v-model="imageStyles.width" class="form-input" placeholder="100%">
                </div>
                <div class="style-group">
                  <label>Alto</label>
                  <input type="text" v-model="imageStyles.height" class="form-input" placeholder="auto">
                </div>
                <div class="style-group">
                  <label>Texto Alt</label>
                  <input type="text" v-model="imageStyles.alt" class="form-input" placeholder="Descripción">
                </div>
                <div class="style-group">
                  <label>Alineación</label>
                  <select v-model="imageStyles.align" class="form-select">
                    <option value="left">Izquierda</option>
                    <option value="center">Centro</option>
                    <option value="right">Derecha</option>
                  </select>
                </div>
                <div class="style-group">
                  <label>Leyenda</label>
                  <input type="text" v-model="imageStyles.caption" class="form-input" placeholder="Leyenda opcional">
                </div>
                <div class="style-group">
                  <label>Color de Leyenda</label>
                  <div class="color-picker">
                    <input type="color" v-model="imageStyles.captionColor">
                    <input type="text" v-model="imageStyles.captionColor">
                  </div>
                </div>
                <div class="style-group">
                  <label>Border Radius</label>
                  <input type="text" v-model="imageStyles.borderRadius" class="form-input" placeholder="0px">
                </div>
                <div class="style-group">
                  <label>Borde</label>
                  <input type="text" v-model="imageStyles.border" class="form-input" placeholder="none">
                </div>
                <div class="style-group">
                  <label>Padding</label>
                  <input type="text" v-model="imageStyles.padding" class="form-input" placeholder="10px">
                </div>
                <div class="style-group">
                  <label>URL de Enlace</label>
                  <input type="url" v-model="imageStyles.linkUrl" class="form-input" placeholder="https://...">
                </div>
              </div>
            </div>
          </div>

          <!-- Editor de Botón -->
          <div v-if="componentType === 'ButtonBlock'" class="button-editor">
            <!-- Texto del Botón -->
            <div class="editor-section">
              <label>Texto del Botón</label>
              <input 
                type="text" 
                v-model="buttonConfig.text"
                placeholder="Texto del botón"
                class="form-input"
              >
            </div>

            <!-- URL del Botón -->
            <div class="editor-section">
              <label>URL de Destino</label>
              <input 
                type="url" 
                v-model="buttonConfig.url"
                placeholder="https://ejemplo.com"
                class="form-input"
              >
            </div>

            <!-- Estilos del Botón -->
            <div class="editor-section">
              <h4>Personalización</h4>
              
              <!-- Color de Fondo -->
              <div class="style-option">
                <label>Color de Fondo</label>
                <div class="color-picker">
                  <input 
                    type="color" 
                    v-model="buttonConfig.styles.backgroundColor"
                  >
                  <input 
                    type="text" 
                    v-model="buttonConfig.styles.backgroundColor"
                    placeholder="#000000"
                  >
                </div>
              </div>

              <!-- Color de Texto -->
              <div class="style-option">
                <label>Color de Texto</label>
                <div class="color-picker">
                  <input 
                    type="color" 
                    v-model="buttonConfig.styles.color"
                  >
                  <input 
                    type="text" 
                    v-model="buttonConfig.styles.color"
                    placeholder="#FFFFFF"
                  >
                </div>
              </div>

              <!-- Alineación -->
              <div class="style-option">
                <label>Alineación</label>
                <div class="button-group">
                  <button 
                    v-for="align in alignments" 
                    :key="align.value"
                    :class="['align-btn', { active: buttonConfig.styles.align === align.value }]"
                    @click="buttonConfig.styles.align = align.value"
                  >
                    <i :class="align.icon"></i>
                  </button>
                </div>
              </div>

              <!-- Vista Previa -->
              <div class="preview-section">
                <label>Vista Previa</label>
                <div class="button-preview" :style="previewStyles">
                  <a 
                    :style="buttonStyles" 
                    class="preview-button"
                    @click.prevent
                  >
                    {{ buttonConfig.text || 'Texto del Botón' }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Editor de Espaciador -->
          <div v-if="componentType === 'SpacerBlock'" class="spacer-editor">
            <div class="style-grid">
              <div class="style-group">
                <label>Altura</label>
                <input type="text" v-model="spacerStyles.height" class="form-input" placeholder="40px">
              </div>
              <div class="style-group">
                <label>Color de Fondo</label>
                <div class="color-picker">
                  <input type="color" v-model="spacerStyles.backgroundColor">
                  <input type="text" v-model="spacerStyles.backgroundColor" placeholder="transparent">
                </div>
              </div>
              <div class="style-group">
                <label>Borde Superior</label>
                <input type="text" v-model="spacerStyles.borderTop" class="form-input" placeholder="none">
              </div>
              <div class="style-group">
                <label>Borde Inferior</label>
                <input type="text" v-model="spacerStyles.borderBottom" class="form-input" placeholder="none">
              </div>
              <div class="style-group">
                <label>Margen</label>
                <input type="text" v-model="spacerStyles.margin" class="form-input" placeholder="0">
              </div>
            </div>
          </div>

          <!-- Editor de Divisor -->
          <div v-if="componentType === 'DividerBlock'" class="divider-editor">
            <div class="style-grid">
              <div class="style-group">
                <label>Color</label>
                <div class="color-picker">
                  <input type="color" v-model="dividerStyles.color">
                  <input type="text" v-model="dividerStyles.color">
                </div>
              </div>
              <div class="style-group">
                <label>Estilo</label>
                <select v-model="dividerStyles.style" class="form-select">
                  <option value="solid">Sólido</option>
                  <option value="dashed">Punteado</option>
                  <option value="dotted">Puntos</option>
                  <option value="double">Doble</option>
                  <option value="groove">Groove</option>
                  <option value="ridge">Ridge</option>
                  <option value="gradient">Gradiente</option>
                </select>
              </div>
              <div class="style-group">
                <label>Ancho</label>
                <input type="text" v-model="dividerStyles.width" class="form-input" placeholder="100%">
              </div>
              <div class="style-group">
                <label>Grosor</label>
                <input type="text" v-model="dividerStyles.thickness" class="form-input" placeholder="1px">
              </div>
              <div class="style-group">
                <label>Alineación</label>
                <select v-model="dividerStyles.align" class="form-select">
                  <option value="left">Izquierda</option>
                  <option value="center">Centro</option>
                  <option value="right">Derecha</option>
                </select>
              </div>
              <div class="style-group">
                <label>Margen</label>
                <input type="text" v-model="dividerStyles.margin" class="form-input" placeholder="20px 0">
              </div>
              <div class="style-group">
                <label>Opacidad</label>
                <input type="range" v-model="dividerStyles.opacity" min="0" max="1" step="0.1" class="form-range">
                <span>{{ dividerStyles.opacity }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="$emit('close')">
            <i class="fas fa-times"></i> Cancelar
          </button>
          <button class="btn btn-primary" @click="handleSave">
            <i class="fas fa-save"></i> Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  width: 800px;
  min-height: 400px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  position: relative;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
  color: #2c3e50;
}

.modal-close {
  position: absolute;
  right: 1rem;
  top: 0.75rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.modal-close:hover {
  color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.modal-body {
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  overflow: hidden;
}

.full-height-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
}

:deep(.text-editor) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.ql-container) {
  flex: 1;
  overflow: auto;
}

:deep(.ql-editor) {
  min-height: 200px;
}

:deep(.ql-toolbar) {
  border: 1px solid #e9ecef;
  border-radius: 4px 4px 0 0;
  background: #f8f9fa;
}

:deep(.ql-container) {
  border: 1px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 4px 4px;
}

.modal-footer {
  padding: 1rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* Animaciones */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.image-editor {
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
}

.drop-zone {
  flex: 1;
  border: 2px dashed #ced4da;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: #f8f9fa;
  min-height: 300px;
}

.drop-zone--active {
  border-color: #007bff;
  background: rgba(0, 123, 255, 0.1);
}

.drop-content {
  text-align: center;
  color: #6c757d;
}

.drop-content i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #007bff;
}

.file-input {
  display: none;
}

.image-preview {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.image-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.remove-image {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-image:hover {
  background: #dc3545;
  transform: scale(1.1);
}

.button-editor {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.editor-section {
  background: white;
  padding: 1.25rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.editor-section h4 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1rem;
  font-weight: 600;
}

.style-option {
  margin-bottom: 1rem;
}

.form-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  margin-top: 0.5rem;
}

.color-picker {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.color-picker input[type="color"] {
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.color-picker input[type="text"] {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.button-group {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.align-btn {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.align-btn.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.preview-section {
  margin-top: 1.5rem;
}

.button-preview {
  margin-top: 0.5rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

.preview-button {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0.5rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  color: #495057;
  font-weight: 500;
}

/* Nuevos estilos para editores mejorados */
.text-editor-container,
.image-editor-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.editor-tabs {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 20px;
}

.tab-btn {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.tab-btn.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

.tab-btn:hover {
  background: rgba(59, 130, 246, 0.1);
}

.tab-content {
  flex: 1;
  overflow-y: auto;
}

.style-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  padding: 16px 0;
}

.style-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-range {
  width: 100%;
  margin: 8px 0;
}

.image-url-input {
  margin-bottom: 20px;
}

.divider-text {
  text-align: center;
  color: #64748b;
  font-weight: 500;
  margin: 20px 0;
  position: relative;
}

.divider-text::before,
.divider-text::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background: #e2e8f0;
}

.divider-text::before {
  left: 0;
}

.divider-text::after {
  right: 0;
}

.spacer-editor,
.divider-editor {
  padding: 20px;
}

/* Mejoras visuales */
.color-picker input[type="color"] {
  width: 50px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
}

.color-picker input[type="text"] {
  font-family: 'Courier New', monospace;
  font-size: 13px;
}

.style-group label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

/* Responsive */
@media (max-width: 768px) {
  .style-grid {
    grid-template-columns: 1fr;
  }
  
  .editor-tabs {
    flex-wrap: wrap;
  }
  
  .tab-btn {
    min-width: 120px;
  }
}
</style>

<script>
import TextEditor from './editors/TextEditor.vue'

export default {
  name: 'Modal',
  components: {
    TextEditor
  },
  
  props: {
    title: {
      type: String,
      default: 'Editar Contenido'
    },
    componentType: {
      type: String,
      default: ''
    },
    initialContent: {
      type: [String, Object],
      default: ''
    }
  },

  data() {
    return {
      editedContent: this.initialContent,
      isDragging: false,
      imagePreview: null,
      
      // Tabs activos
      activeTextTab: 'content',
      activeImageTab: 'upload',
      
      // Configuraciones de estilos
      textStyles: {
        fontSize: '16px',
        color: '#2c3e50',
        backgroundColor: 'transparent',
        textAlign: 'left',
        fontFamily: 'Arial, sans-serif',
        fontWeight: '400',
        lineHeight: '1.5',
        padding: '16px',
        margin: '10px 0',
        borderRadius: '0px',
        border: 'none'
      },
      
      imageStyles: {
        width: '100%',
        height: 'auto',
        alt: 'Imagen',
        align: 'center',
        caption: '',
        captionAlign: 'center',
        captionColor: '#6c757d',
        captionSize: '14px',
        borderRadius: '0px',
        border: 'none',
        padding: '10px',
        margin: '10px 0',
        backgroundColor: 'transparent',
        objectFit: 'cover',
        linkUrl: ''
      },
      
      spacerStyles: {
        height: '40px',
        backgroundColor: 'transparent',
        borderTop: 'none',
        borderBottom: 'none',
        margin: '0'
      },
      
      dividerStyles: {
        color: '#e2e8f0',
        style: 'solid',
        width: '100%',
        thickness: '1px',
        align: 'center',
        margin: '20px 0',
        padding: '0',
        backgroundColor: 'transparent',
        borderRadius: '0px',
        opacity: '1'
      },
      
      buttonConfig: {
        text: '',
        url: '',
        styles: {
          backgroundColor: '#007bff',
          color: '#FFFFFF',
          align: 'center'
        }
      },
      alignments: [
        { value: 'left', icon: 'fas fa-align-left' },
        { value: 'center', icon: 'fas fa-align-center' },
        { value: 'right', icon: 'fas fa-align-right' }
      ]
    }
  },

  computed: {
    buttonStyles() {
      return {
        backgroundColor: this.buttonConfig.styles.backgroundColor,
        color: this.buttonConfig.styles.color,
      }
    },
    previewStyles() {
      return {
        textAlign: this.buttonConfig.styles.align,
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }
    }
  },

  created() {
    // Inicializar configuraciones según el tipo de componente
    if (this.componentType === 'ButtonBlock' && this.initialContent) {
      try {
        const content = typeof this.initialContent === 'string' 
          ? JSON.parse(this.initialContent)
          : this.initialContent
        
        this.buttonConfig = {
          ...this.buttonConfig,
          ...content
        }
      } catch (e) {
        console.error('Error parsing button config:', e)
      }
    }

    // Cargar estilos iniciales para TextBlock
    if (this.componentType === 'TextBlock' && this.initialStyles) {
      this.textStyles = {
        ...this.textStyles,
        ...this.initialStyles
      }
    }

    // Cargar estilos iniciales para ImageBlock
    if (this.componentType === 'ImageBlock' && this.initialStyles) {
      this.imageStyles = {
        ...this.imageStyles,
        ...this.initialStyles
      }
      if (this.initialContent) {
        this.imagePreview = this.initialContent
      }
    }

    // Cargar estilos iniciales para SpacerBlock
    if (this.componentType === 'SpacerBlock' && this.initialStyles) {
      this.spacerStyles = {
        ...this.spacerStyles,
        ...this.initialStyles
      }
    }

    // Cargar estilos iniciales para DividerBlock
    if (this.componentType === 'DividerBlock' && this.initialStyles) {
      this.dividerStyles = {
        ...this.dividerStyles,
        ...this.initialStyles
      }
    }
  },

  methods: {
    updateContent(value) {
      this.editedContent = value
    },

    handleDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.handleImage(file)
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.handleImage(file)
      }
    },

    handleImage(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imagePreview = e.target.result
        this.editedContent = e.target.result
      }
      reader.readAsDataURL(file)
    },

    removeImage() {
      this.imagePreview = null
      this.editedContent = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    handleSave() {
      let saveData = {}

      switch (this.componentType) {
        case 'TextBlock':
          saveData = {
            content: this.editedContent,
            styles: this.textStyles
          }
          break

        case 'ImageBlock':
          saveData = {
            content: this.editedContent,
            styles: this.imageStyles
          }
          break

        case 'ButtonBlock':
          saveData = {
            content: this.buttonConfig
          }
          break

        case 'SpacerBlock':
          saveData = {
            content: '',
            styles: this.spacerStyles
          }
          break

        case 'DividerBlock':
          saveData = {
            content: '',
            styles: this.dividerStyles
          }
          break

        default:
          saveData = {
            content: this.editedContent
          }
      }

      this.$emit('save', saveData)
    }
  }
}
</script> 